import React, { useState, useEffect } from 'react';
import {
    X, Plus, Trash2, GripVertical, MessageSquare, GitBranch,
    Clock, Tag, ShoppingCart, Zap, ChevronDown, AlertCircle
} from 'lucide-react';
import { NODE_TYPES } from './FlowNode';

const NodeEditor = ({ node, onUpdate, onClose, products = [] }) => {
    const [data, setData] = useState(node?.data || {});
    const nodeType = NODE_TYPES[node?.type] || {};

    useEffect(() => {
        setData(node?.data || {});
    }, [node?.id]);

    const handleChange = (field, value) => {
        const newData = { ...data, [field]: value };
        setData(newData);
        onUpdate?.(node.id, { data: newData });
    };

    const handleBranchChange = (index, field, value) => {
        const branches = [...(data.branches || [])];
        branches[index] = { ...branches[index], [field]: value };
        handleChange('branches', branches);
    };

    const addBranch = () => {
        const branches = [...(data.branches || [])];
        branches.push({ label: `Option ${branches.length + 1}`, condition: '' });
        handleChange('branches', branches);
    };

    const removeBranch = (index) => {
        const branches = [...(data.branches || [])];
        branches.splice(index, 1);
        handleChange('branches', branches);
    };

    if (!node) return null;

    return (
        <div className="w-80 bg-[#0d1424] border-l border-white/10 flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <nodeType.icon size={18} className="text-[#ff4d4d]" />
                    <h3 className="font-semibold text-white">{nodeType.label}</h3>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                        Step Name
                    </label>
                    <input
                        type="text"
                        value={data.title || ''}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder={nodeType.label}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition text-sm"
                    />
                </div>

                {/* Start Node */}
                {node.type === 'start' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Trigger
                            </label>
                            <select
                                value={data.trigger || 'conversation_start'}
                                onChange={(e) => handleChange('trigger', e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                            >
                                <option value="conversation_start">When conversation starts</option>
                                <option value="keyword">When keyword detected</option>
                                <option value="product_inquiry">When product inquiry</option>
                                <option value="manual">Manual trigger</option>
                            </select>
                        </div>
                        {data.trigger === 'keyword' && (
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Keywords (comma separated)
                                </label>
                                <input
                                    type="text"
                                    value={data.keywords || ''}
                                    onChange={(e) => handleChange('keywords', e.target.value)}
                                    placeholder="buy, order, price, interested"
                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 text-sm"
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Message / Question Node */}
                {(node.type === 'message' || node.type === 'question') && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Message
                            </label>
                            <textarea
                                value={data.message || ''}
                                onChange={(e) => handleChange('message', e.target.value)}
                                placeholder="Type your message here..."
                                rows={4}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 resize-none text-sm"
                            />
                            <p className="mt-1 text-xs text-white/40">
                                Use {'{name}'} for customer name, {'{product}'} for product
                            </p>
                        </div>

                        {node.type === 'question' && (
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Expected Response Type
                                </label>
                                <select
                                    value={data.responseType || 'any'}
                                    onChange={(e) => handleChange('responseType', e.target.value)}
                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                                >
                                    <option value="any">Any response</option>
                                    <option value="yes_no">Yes/No</option>
                                    <option value="number">Number</option>
                                    <option value="choice">Multiple choice</option>
                                    <option value="text">Text input</option>
                                </select>
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="waitForResponse"
                                checked={data.waitForResponse !== false}
                                onChange={(e) => handleChange('waitForResponse', e.target.checked)}
                                className="w-4 h-4 rounded border-white/30 bg-white/5 text-[#ff4d4d] focus:ring-[#ff4d4d]/50"
                            />
                            <label htmlFor="waitForResponse" className="text-sm text-white/70">
                                Wait for customer response
                            </label>
                        </div>
                    </>
                )}

                {/* Condition Node */}
                {node.type === 'condition' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Condition Based On
                            </label>
                            <select
                                value={data.conditionType || 'response'}
                                onChange={(e) => handleChange('conditionType', e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                            >
                                <option value="response">Customer response</option>
                                <option value="keyword">Keyword match</option>
                                <option value="sentiment">Sentiment (positive/negative)</option>
                                <option value="intent">Intent detection</option>
                            </select>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-white/70">
                                    Branches
                                </label>
                                <button
                                    onClick={addBranch}
                                    className="text-xs text-[#ff4d4d] hover:text-[#ff6b6b] flex items-center gap-1"
                                >
                                    <Plus size={12} />
                                    Add Branch
                                </button>
                            </div>

                            <div className="space-y-2">
                                {(data.branches || [{ label: 'Yes', condition: '' }, { label: 'No', condition: '' }]).map((branch, index) => (
                                    <div
                                        key={index}
                                        className="p-3 bg-white/5 border border-white/10 rounded-lg space-y-2"
                                    >
                                        <div className="flex items-center gap-2">
                                            <GripVertical size={14} className="text-white/30 cursor-grab" />
                                            <input
                                                type="text"
                                                value={branch.label}
                                                onChange={(e) => handleBranchChange(index, 'label', e.target.value)}
                                                placeholder="Branch name"
                                                className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm focus:outline-none focus:border-[#ff4d4d]"
                                            />
                                            {(data.branches?.length || 2) > 2 && (
                                                <button
                                                    onClick={() => removeBranch(index)}
                                                    className="p-1 text-white/40 hover:text-red-500 transition"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            value={branch.condition}
                                            onChange={(e) => handleBranchChange(index, 'condition', e.target.value)}
                                            placeholder="e.g., contains 'yes', equals '1'"
                                            className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white/70 text-xs focus:outline-none focus:border-[#ff4d4d]"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Default Branch (if no match)
                            </label>
                            <select
                                value={data.defaultBranch || 'last'}
                                onChange={(e) => handleChange('defaultBranch', e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                            >
                                <option value="last">Last branch</option>
                                <option value="first">First branch</option>
                                <option value="none">Stop flow</option>
                            </select>
                        </div>
                    </>
                )}

                {/* Action Node */}
                {node.type === 'action' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Action Type
                            </label>
                            <select
                                value={data.actionType || ''}
                                onChange={(e) => handleChange('actionType', e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                            >
                                <option value="">Select action...</option>
                                <option value="add_tag">Add tag to customer</option>
                                <option value="remove_tag">Remove tag</option>
                                <option value="assign_agent">Assign to agent</option>
                                <option value="send_notification">Send notification</option>
                                <option value="update_crm">Update CRM field</option>
                                <option value="webhook">Call webhook</option>
                            </select>
                        </div>

                        {data.actionType === 'add_tag' && (
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Tag Name
                                </label>
                                <input
                                    type="text"
                                    value={data.tagName || ''}
                                    onChange={(e) => handleChange('tagName', e.target.value)}
                                    placeholder="e.g., hot-lead, interested"
                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 text-sm"
                                />
                            </div>
                        )}

                        {data.actionType === 'webhook' && (
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Webhook URL
                                </label>
                                <input
                                    type="url"
                                    value={data.webhookUrl || ''}
                                    onChange={(e) => handleChange('webhookUrl', e.target.value)}
                                    placeholder="https://..."
                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 text-sm"
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Delay Node */}
                {node.type === 'delay' && (
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Duration
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={data.duration || 1}
                                onChange={(e) => handleChange('duration', parseInt(e.target.value) || 1)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 text-sm"
                            />
                        </div>
                        <div className="w-28">
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Unit
                            </label>
                            <select
                                value={data.unit || 'minute'}
                                onChange={(e) => handleChange('unit', e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                            >
                                <option value="second">Seconds</option>
                                <option value="minute">Minutes</option>
                                <option value="hour">Hours</option>
                                <option value="day">Days</option>
                            </select>
                        </div>
                    </div>
                )}

                {/* Order Node */}
                {node.type === 'order' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Product
                            </label>
                            <select
                                value={data.productId || ''}
                                onChange={(e) => handleChange('productId', e.target.value)}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none text-sm"
                            >
                                <option value="">Select product...</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Collect Info
                            </label>
                            <div className="space-y-2">
                                {['name', 'phone', 'address', 'email'].map(field => (
                                    <label key={field} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={data.collectFields?.includes(field) ?? true}
                                            onChange={(e) => {
                                                const fields = new Set(data.collectFields || ['name', 'phone', 'address']);
                                                if (e.target.checked) fields.add(field);
                                                else fields.delete(field);
                                                handleChange('collectFields', Array.from(fields));
                                            }}
                                            className="w-4 h-4 rounded border-white/30 bg-white/5 text-[#ff4d4d] focus:ring-[#ff4d4d]/50"
                                        />
                                        <span className="text-sm text-white/70 capitalize">{field}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-white/70 mb-2">
                                Success Message
                            </label>
                            <textarea
                                value={data.successMessage || ''}
                                onChange={(e) => handleChange('successMessage', e.target.value)}
                                placeholder="Thank you! Your order has been placed."
                                rows={2}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 resize-none text-sm"
                            />
                        </div>
                    </>
                )}

                {/* Success/Failure Node */}
                {(node.type === 'success' || node.type === 'failure') && (
                    <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">
                            Final Message (optional)
                        </label>
                        <textarea
                            value={data.message || ''}
                            onChange={(e) => handleChange('message', e.target.value)}
                            placeholder="Thank you for your time!"
                            rows={3}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 resize-none text-sm"
                        />
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10">
                <p className="text-xs text-white/40">
                    {nodeType.description}
                </p>
            </div>
        </div>
    );
};

export default NodeEditor;
